import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CatalogDefaultValues } from "@amzn/coral_com-amazon-deviceslaunchcontrolservice";
import { FullAsinWithAudits } from "../../../from-server/asin-central/model";
import { AppThunk } from "../../app/store";
import {
  getCorrectCatalogDefaults,
  publishCorrectCatalogDefaultsBulk,
} from "../../app/api";
import { createNotificationDefinition } from "../../notification/notificationConnector";
import { triggerValidationDashboardRefresh } from "../asinValidationTableConnector";
import {
  ValidationAttribute,
  ValidationAttributeDetailStep,
} from "../validationAttribute";
import { isWithin24Hours } from "../../../util/dateUtils";
import { runWithConcurrency } from "../../../util/concurrency";

export const MAX_BULK_CATALOG_DEFAULTS_ASINS = 1_000;
export const RESOLVE_CONCURRENCY = 8;
export const SUBMIT_CHUNK_SIZE = 50;

// Sentinel value written into `correctedValue` when the resolved
// default is empty (or whitespace-only). Surfaces the informational
// modal warning about manual Selection Central cleanup, and is
// filtered out by the payload builder so a blank value never
// silently overwrites an existing catalog value.
export const BLANK_SENTINEL = "<BLANK>";

export const LVS_ATTRIBUTES_IN_SCOPE: ReadonlySet<string> = new Set([
  "DISPLAY_ATTRIBUTE",
  "DISPLAY_ON_WEBSITE",
  "TITLE",
  "BRAND",
  "BRAND_CODE",
  "GL_PRODUCT_GROUP_TYPE",
  "PRODUCT_CATEGORY",
  "PRODUCT_SUBCATEGORY",
  "PRODUCT_TYPE",
  "SOR_ID",
  "CAN_BE_GIFT_WRAPPED",
  "CAN_BE_GIFT_MESSAGED",
  "ITEM_TYPE_KEYWORD",
  "MANUFACTURER",
  "RECOMMENDED_BROWSE_NODES",
  "FEATURE_BULLETS",
]);

const STEP_TO_FIELD: Record<string, keyof CatalogDefaultValues> = {
  BRAND: "brand",
  BRAND_CODE: "brandCode",
  FEATURE_BULLETS: "bulletPoint",
  CAN_BE_GIFT_MESSAGED: "deprecatedCanBeGiftmessaged",
  CAN_BE_GIFT_WRAPPED: "deprecatedCanBeGiftwrapped",
  DISPLAY_ATTRIBUTE: "displayAttribute",
  DISPLAY_ON_WEBSITE: "displayOnWebsite",
  GL_PRODUCT_GROUP_TYPE: "glProductTypeGroupType",
  ITEM_TYPE_KEYWORD: "itemTypeKeyword",
  MANUFACTURER: "manufacturer",
  PRODUCT_CATEGORY: "productCategory",
  PRODUCT_SUBCATEGORY: "productSubCategory",
  PRODUCT_TYPE: "productType",
  RECOMMENDED_BROWSE_NODES: "recommendedBrowseNodes",
  SOR_ID: "sellerOfRecordVendorId",
  TITLE: "title",
};

export type SkipReason =
  | "lvs_stale"
  | "no_in_scope_failures"
  | "defaults_unresolved"
  | "no_matching_defaults";

const SKIP_REASON_TEXT: Record<SkipReason, string> = {
  lvs_stale: "LVS validation older than 24 hours",
  no_in_scope_failures: "No failed steps eligible for catalog corrections",
  defaults_unresolved:
    "Make sure default values are loaded for this ASIN's product line before retrying",
  no_matching_defaults:
    "Resolved defaults do not contain values for any failed step",
};

export interface BulkCatalogDefaultsRow {
  readonly key: string;
  readonly asin: string;
  readonly obfuscatedMarketplaceId: string;
  readonly marketplace: string;
  readonly asinType: string;
  readonly programName: string;
  readonly step: string;
  readonly currentValue: string;
  readonly correctedValue: string;
  readonly reason: string;
}

export interface SkippedCatalogDefaultsAsin {
  readonly asin: string;
  readonly marketplace: string;
  readonly programName: string;
  readonly reason: SkipReason;
  readonly reasonText: string;
}

export interface SubmitCatalogDefaultsState {
  readonly isOpen: boolean;
  readonly workingAsins: FullAsinWithAudits[];
  readonly isFetching: boolean;
  readonly fetchError: string | undefined;
  // Whether the working set was capped at MAX_BULK_CATALOG_DEFAULTS_ASINS
  // during the pagination fetch. Surfaced as a warning in the modal so
  // the user knows they'd only be submitting a partial slice.
  readonly fetchTruncated: boolean;
  readonly isResolving: boolean;
  readonly resolveAttempted: boolean;
  readonly resolvedTuples: Record<string, CatalogDefaultValues | null>;
  readonly isSubmitting: boolean;
  readonly submissionCompleted: boolean;
  // `submissionCompleted` marks the async work as done. `submissionSucceeded`
  // reports whether at least one chunk succeeded, so the modal can decide
  // whether to auto-close (some success -> close) or stay open (total
  // failure -> keep selection visible for retry).
  readonly submissionSucceeded: boolean;
}

const initialState: SubmitCatalogDefaultsState = {
  isOpen: false,
  workingAsins: [],
  isFetching: false,
  fetchError: undefined,
  fetchTruncated: false,
  isResolving: false,
  resolveAttempted: false,
  resolvedTuples: {},
  isSubmitting: false,
  submissionCompleted: false,
  submissionSucceeded: false,
};

const slice = createSlice({
  name: "submitCatalogDefaults",
  initialState,
  reducers: {
    openWithAsins(
      state,
      action: PayloadAction<{ readonly asins: FullAsinWithAudits[] }>
    ) {
      state.isOpen = true;
      state.workingAsins = action.payload.asins;
      state.isFetching = false;
      state.fetchError = undefined;
      state.fetchTruncated = false;
      state.isResolving = false;
      state.resolveAttempted = false;
      state.resolvedTuples = {};
      state.isSubmitting = false;
      state.submissionCompleted = false;
      state.submissionSucceeded = false;
    },
    openAndFetch(state) {
      state.isOpen = true;
      state.workingAsins = [];
      state.isFetching = true;
      state.fetchError = undefined;
      state.fetchTruncated = false;
      state.isResolving = false;
      state.resolveAttempted = false;
      state.resolvedTuples = {};
      state.isSubmitting = false;
      state.submissionCompleted = false;
      state.submissionSucceeded = false;
    },
    fetchSucceeded(
      state,
      action: PayloadAction<{
        readonly asins: FullAsinWithAudits[];
        readonly truncated: boolean;
      }>
    ) {
      state.workingAsins = action.payload.asins;
      state.isFetching = false;
      state.fetchError = undefined;
      state.fetchTruncated = action.payload.truncated;
      // A new working set invalidates any prior resolve attempt.
      state.resolveAttempted = false;
      state.resolvedTuples = {};
    },
    fetchFailed(state, action: PayloadAction<{ readonly message: string }>) {
      state.workingAsins = [];
      state.isFetching = false;
      state.fetchError = action.payload.message;
      state.fetchTruncated = false;
      state.resolveAttempted = true;
    },
    resolveStarted(state) {
      state.isResolving = true;
    },
    resolveTupleSet(
      state,
      action: PayloadAction<{
        readonly tupleKey: string;
        readonly values: CatalogDefaultValues | null;
      }>
    ) {
      state.resolvedTuples[action.payload.tupleKey] = action.payload.values;
    },
    resolveFinished(state) {
      state.isResolving = false;
      state.resolveAttempted = true;
    },
    submissionStarted(state) {
      state.isSubmitting = true;
      state.submissionCompleted = false;
      state.submissionSucceeded = false;
    },
    submissionFinished(
      state,
      action: PayloadAction<{ readonly hadSuccess: boolean }>
    ) {
      state.isSubmitting = false;
      state.submissionCompleted = true;
      state.submissionSucceeded = action.payload.hadSuccess;
    },
    closeModal() {
      return initialState;
    },
  },
});

export const {
  openWithAsins,
  openAndFetch,
  fetchSucceeded,
  fetchFailed,
  resolveStarted,
  resolveTupleSet,
  resolveFinished,
  submissionStarted,
  submissionFinished,
  closeModal,
} = slice.actions;

export default slice.reducer;

// -----------------------------------------------------------------
// Pure helpers
// -----------------------------------------------------------------

function safeParseValidationAttribute(
  raw: string | undefined
): ValidationAttribute | undefined {
  if (!raw) {
    return undefined;
  }
  try {
    return JSON.parse(unescape(raw)) as ValidationAttribute;
  } catch {
    return undefined;
  }
}

function toEpochMs(date: string | Date | undefined): number | "" {
  if (!date) {
    return "";
  }
  const d = date instanceof Date ? date : new Date(date);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : "";
}

export interface TupleDescriptor {
  readonly tupleKey: string;
  readonly announceDate: Date | undefined;
  readonly streetDate: Date | undefined;
  readonly marketplace: string;
  readonly productLineId: string;
  readonly asinParty: string;
  readonly asinType: string;
  readonly itemType?: string;
}

export function tupleDescriptorForAsin(
  asin: FullAsinWithAudits
): TupleDescriptor | undefined {
  const marketplace = asin.marketplace;
  const productLineId = asin.productLineId;
  const asinParty = asin.asinParty;
  const asinType = asin.asinType;
  if (!marketplace || !productLineId || !asinParty || !asinType) {
    return undefined;
  }
  const itemType = asin.itemType ? asin.itemType : "UNK";
  const announceDate = asin.announceDate
    ? new Date(asin.announceDate)
    : undefined;
  const streetDate = asin.onlineStreetDate
    ? new Date(asin.onlineStreetDate)
    : undefined;
  const tupleKey = [
    marketplace,
    productLineId,
    asinParty,
    asinType,
    itemType,
    String(toEpochMs(announceDate)),
    String(toEpochMs(streetDate)),
  ].join("|");
  return {
    tupleKey,
    announceDate,
    streetDate,
    marketplace,
    productLineId,
    asinParty,
    asinType,
    itemType,
  };
}

function failedInScopeSteps(
  asin: FullAsinWithAudits
): ValidationAttributeDetailStep[] {
  const parsed = safeParseValidationAttribute(
    asin.validationDetailResultLVS as string | undefined
  );
  if (!parsed || !Array.isArray(parsed.detail)) {
    return [];
  }
  return parsed.detail.filter(
    (step) => step.result === "fail" && LVS_ATTRIBUTES_IN_SCOPE.has(step.step)
  );
}

function lvsFresh(asin: FullAsinWithAudits): boolean {
  const last = asin.validationRequestLVSAudits?.lastUpdatedAt;
  if (!last) {
    return false;
  }
  return isWithin24Hours(new Date(last));
}

export interface BuildRowsResult {
  readonly rows: BulkCatalogDefaultsRow[];
  readonly skipped: SkippedCatalogDefaultsAsin[];
}

export function buildBulkCatalogDefaultsRows(
  asins: readonly FullAsinWithAudits[],
  resolvedTuples: Record<string, CatalogDefaultValues | null>
): BuildRowsResult {
  const rows: BulkCatalogDefaultsRow[] = [];
  const skipped: SkippedCatalogDefaultsAsin[] = [];

  for (const asin of asins) {
    const skippedEntry = (reason: SkipReason): SkippedCatalogDefaultsAsin => ({
      asin: asin.asin,
      marketplace: asin.marketplace ?? "",
      programName: asin.programName ?? "",
      reason,
      reasonText: SKIP_REASON_TEXT[reason],
    });

    if (!lvsFresh(asin)) {
      skipped.push(skippedEntry("lvs_stale"));
      continue;
    }

    const failedSteps = failedInScopeSteps(asin);
    if (failedSteps.length === 0) {
      skipped.push(skippedEntry("no_in_scope_failures"));
      continue;
    }

    const tuple = tupleDescriptorForAsin(asin);
    if (!tuple) {
      skipped.push(skippedEntry("defaults_unresolved"));
      continue;
    }
    const resolved = resolvedTuples[tuple.tupleKey];
    if (resolved === undefined) {
      // Resolution still pending; treat as not-yet-eligible.
      continue;
    }
    if (resolved === null) {
      skipped.push(skippedEntry("defaults_unresolved"));
      continue;
    }

    let emittedForAsin = 0;
    for (const step of failedSteps) {
      const field = STEP_TO_FIELD[step.step];
      if (!field) {
        continue;
      }
      const raw = (resolved as Record<string, unknown>)[field];
      if (raw === undefined || raw === null) {
        continue;
      }
      const asString = String(raw);
      // Empty / whitespace-only resolved values are flagged with the
      // `<BLANK>` sentinel so the modal's manual-cleanup warning
      // renders and the payload builder skips them (no silent clear
      // of an existing catalog value).
      const correctedValue = asString.trim() === "" ? BLANK_SENTINEL : asString;
      rows.push({
        key: `${asin.asin}::${asin.marketplace}::${step.step}`,
        asin: asin.asin,
        obfuscatedMarketplaceId: asin.obfuscatedMarketplaceId,
        marketplace: asin.marketplace,
        asinType: asin.asinType,
        programName: asin.programName ?? "",
        step: step.step,
        currentValue: step.hint ?? "",
        correctedValue,
        reason: step.reason ?? "",
      });
      emittedForAsin += 1;
    }

    if (emittedForAsin === 0) {
      skipped.push(skippedEntry("no_matching_defaults"));
    }
  }

  return { rows, skipped };
}

// -----------------------------------------------------------------
// Thunks
// -----------------------------------------------------------------

function uniqueTupleDescriptors(
  asins: readonly FullAsinWithAudits[]
): TupleDescriptor[] {
  const seen = new Map<string, TupleDescriptor>();
  for (const asin of asins) {
    if (!lvsFresh(asin) || failedInScopeSteps(asin).length === 0) {
      continue;
    }
    const desc = tupleDescriptorForAsin(asin);
    if (!desc) {
      continue;
    }
    if (!seen.has(desc.tupleKey)) {
      seen.set(desc.tupleKey, desc);
    }
  }
  return Array.from(seen.values());
}

export const resolveCatalogDefaultsForAsins =
  (asins: readonly FullAsinWithAudits[]): AppThunk =>
  async (dispatch) => {
    dispatch(resolveStarted());
    const uniqueTuples = uniqueTupleDescriptors(asins);
    const tasks = uniqueTuples.map((tuple) => async () => {
      try {
        const output = await getCorrectCatalogDefaults(
          tuple.announceDate,
          tuple.streetDate,
          tuple.marketplace,
          tuple.productLineId,
          tuple.asinParty,
          tuple.asinType,
          tuple.itemType
        );
        dispatch(
          resolveTupleSet({
            tupleKey: tuple.tupleKey,
            values: output.catalogDefaultValues ?? null,
          })
        );
      } catch {
        dispatch(resolveTupleSet({ tupleKey: tuple.tupleKey, values: null }));
      }
    });
    await runWithConcurrency(tasks, RESOLVE_CONCURRENCY);
    dispatch(resolveFinished());
  };

function catalogDefaultsPayloadForAsin(
  rows: readonly BulkCatalogDefaultsRow[]
):
  | {
      readonly asin: string;
      readonly obfuscatedMarketplaceId: string;
      readonly asinType: string;
      readonly catalogDefaultsValues: Record<string, unknown>;
    }
  | undefined {
  const first = rows[0];
  const values: Record<string, unknown> = {};
  for (const row of rows) {
    const field = STEP_TO_FIELD[row.step];
    if (!field) {
      continue;
    }
    // BLANK_SENTINEL rows are surfaced in the modal (with a warning
    // that Selection Central cleanup is manual) but never included
    // in the payload, so a blank resolved default cannot silently
    // clear an existing catalog value.
    if (row.correctedValue === BLANK_SENTINEL) {
      continue;
    }
    if (
      field === "deprecatedCanBeGiftmessaged" ||
      field === "deprecatedCanBeGiftwrapped"
    ) {
      values[field] = row.correctedValue === "true";
    } else {
      values[field] = row.correctedValue;
    }
  }
  if (Object.keys(values).length === 0) {
    return undefined;
  }
  return {
    asin: first.asin,
    obfuscatedMarketplaceId: first.obfuscatedMarketplaceId,
    asinType: first.asinType,
    catalogDefaultsValues: values,
  };
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

export const submitBulkCatalogDefaults =
  (selectedRows: readonly BulkCatalogDefaultsRow[]): AppThunk =>
  async (dispatch) => {
    dispatch(submissionStarted());

    // Group rows by ASIN → one CorrectCatalogDefaults payload per ASIN.
    const byAsin = new Map<string, BulkCatalogDefaultsRow[]>();
    for (const row of selectedRows) {
      const k = `${row.asin}::${row.marketplace}`;
      const list = byAsin.get(k);
      if (list) {
        list.push(row);
      } else {
        byAsin.set(k, [row]);
      }
    }
    // Drop per-ASIN payloads whose fields were all BLANK_SENTINEL —
    // there's nothing to submit for those; the user has to clean them
    // up manually via Selection Central.
    const perAsinPayloads = Array.from(byAsin.values())
      .map(catalogDefaultsPayloadForAsin)
      .filter(
        (
          p
        ): p is {
          readonly asin: string;
          readonly obfuscatedMarketplaceId: string;
          readonly asinType: string;
          readonly catalogDefaultsValues: Record<string, unknown>;
        } => p !== undefined
      );

    if (perAsinPayloads.length === 0) {
      dispatch(
        createNotificationDefinition(
          "warning",
          "No Catalog Defaults to Submit",
          true,
          "All selected corrections resolved to <BLANK>. Delete the affected" +
            " contributions manually in Selection Central."
        )
      );
      dispatch(submissionFinished({ hadSuccess: false }));
      return;
    }

    const chunks = chunk(perAsinPayloads, SUBMIT_CHUNK_SIZE);
    let successAsinCount = 0;
    let failedAsinCount = 0;
    const failureMessages: string[] = [];
    const workflowIds: string[] = [];

    for (const c of chunks) {
      try {
        const output = await publishCorrectCatalogDefaultsBulk(c);
        successAsinCount += c.length;
        if (output.workflowId) {
          workflowIds.push(output.workflowId);
        }
      } catch (err: any) {
        failedAsinCount += c.length;
        const detail =
          err?.response?.data?.message?.toString() ??
          err?.message ??
          "unknown error";
        failureMessages.push(detail);
      }
    }

    const pluralAsins = (n: number): string =>
      n === 1 ? "1 ASIN" : `${n} ASINs`;

    if (failedAsinCount === 0) {
      dispatch(
        createNotificationDefinition(
          "success",
          "Submitted Catalog Defaults",
          true,
          `Submitted catalog defaults for ${pluralAsins(successAsinCount)}.` +
            (workflowIds.length > 0
              ? ` Workflow IDs: ${workflowIds.slice(0, 3).join(", ")}${
                  workflowIds.length > 3 ? ", ..." : ""
                }.`
              : "") +
            " It may take up to 24 hours for values to reconcile in Catalog."
        )
      );
    } else if (successAsinCount === 0) {
      dispatch(
        createNotificationDefinition(
          "error",
          "Failed to Submit Catalog Defaults",
          true,
          `Failed to submit catalog defaults for ${pluralAsins(
            failedAsinCount
          )}. First failures: ` + failureMessages.slice(0, 3).join(" | ")
        )
      );
    } else {
      dispatch(
        createNotificationDefinition(
          "warning",
          "Partial Catalog Defaults Submission",
          true,
          `Submitted catalog defaults for ${pluralAsins(
            successAsinCount
          )} of ${pluralAsins(
            successAsinCount + failedAsinCount
          )}. First failures: ` + failureMessages.slice(0, 3).join(" | ")
        )
      );
    }

    dispatch(submissionFinished({ hadSuccess: successAsinCount > 0 }));

    if (successAsinCount > 0) {
      dispatch(triggerValidationDashboardRefresh());
    }
  };
